import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api } from '../../lib/api.js'
import {
  Badge, ErrorBox, Field, Page, Spinner, SubmitButton, useAsync, useConfirm, useToast
} from '../../components/Ui.jsx'
import { fmtDate, fmtStamp, inr, lakh } from '../../lib/format.js'

export default function TripDetail () {
  const { id } = useParams()
  const toast = useToast()
  const confirm = useConfirm()
  const { data, error, loading, reload } = useAsync(() => api.get(`/liaison/trips/${id}`), [id], { live: ['trips'] })
  const [packing, setPacking] = useState({ bagsLoaded: '', waterTempC: '', salinityPpt: '' })
  const [photo, setPhoto] = useState(null)
  const [delivery, setDelivery] = useState({ otp: '', damagedBags: 0, conditionNotes: '' })
  const [busy, setBusy] = useState(false)

  const submitPacking = async e => {
    e.preventDefault()
    const ok = await confirm({
      title: 'Log the packing and issue the gate pass?',
      body: 'Count the bags on the vehicle before you commit this — the gate pass goes out on these numbers.',
      facts: [
        ['Bags loaded', String(packing.bagsLoaded || '—')],
        ['Water temp', packing.waterTempC ? `${packing.waterTempC} °C` : 'not recorded'],
        ['Salinity', packing.salinityPpt ? `${packing.salinityPpt} ppt` : 'not recorded'],
        ['Photo', photo ? photo.name : 'none attached']
      ],
      confirmLabel: 'Log packing'
    })
    if (!ok) return
    setBusy(true)
    try {
      const fd = new FormData()
      fd.append('bagsLoaded', packing.bagsLoaded)
      if (packing.waterTempC) fd.append('waterTempC', packing.waterTempC)
      if (packing.salinityPpt) fd.append('salinityPpt', packing.salinityPpt)
      if (photo) fd.append('photo', photo)
      const res = await api.upload(`/liaison/trips/${id}/packing`, fd)
      toast.ok(res.message)
      reload()
    } catch (err) { toast.error(err.message) } finally { setBusy(false) }
  }

  const depart = async () => {
    const ok = await confirm({
      title: 'Mark the vehicle as departed?',
      body: 'The desk and the farmer both start counting the haul from this moment.',
      confirmLabel: 'We have left'
    })
    if (!ok) return
    setBusy(true)
    try {
      const res = await api.post(`/liaison/trips/${id}/depart`, {})
      toast.ok(res.message)
      reload()
    } catch (err) { toast.error(err.message) } finally { setBusy(false) }
  }

  const submitDelivery = async e => {
    e.preventDefault()
    const ok = await confirm({
      title: 'Complete the handover at the pond?',
      body: 'Confirm only with the farmer standing with you. This closes the trip and records the seed as delivered.',
      facts: [
        ['OTP from the farmer', delivery.otp || 'not entered'],
        ['Damaged bags', String(delivery.damagedBags || 0)],
        ['Notes', delivery.conditionNotes || 'none']
      ],
      confirmLabel: 'Complete handover'
    })
    if (!ok) return
    setBusy(true)
    try {
      const res = await api.post(`/liaison/trips/${id}/deliver`, delivery)
      toast.ok(res.message)
      reload()
    } catch (err) { toast.error(err.message) } finally { setBusy(false) }
  }

  if (loading) return <Page title="Trip"><Spinner /></Page>
  if (error) return <Page title="Trip"><ErrorBox error={error} onRetry={reload} /></Page>

  const t = data.trip
  const o = t.order
  const b = o?.batch

  return (
    <Page
      crumb={<Link to="/liaison">← My trips</Link>}
      title={t.tripCode}
      subtitle={`${o?.hatchery?.name} → ${o?.farmer?.name} · ${t.distanceKm} km · about ${t.estHours} hours`}
    >
      <div className="phone">
        <div className="phead">
          <b>{o?.farmer?.name}</b>
          <div className="small" style={{ opacity: .85 }}>
            {[o?.farmer?.village, o?.farmer?.mandal, o?.farmer?.district].filter(Boolean).join(', ')}<br />
            {o?.farmer?.phone} · stocking {fmtDate(o?.targetStockingDate)}
          </div>
        </div>

        <div className="pbody">
          <div className="stepcard">
            <h3>Load</h3>
            <div className="kv">
              <div><div className="k">Seed</div><div className="v">{lakh(o?.quantityLakh)} {o?.plStage}</div></div>
              <div><div className="k">Batch</div><div className="v mono" style={{ fontSize: 13 }}>{b?.batchCode}</div></div>
              <div><div className="k">Vehicle</div><div className="v">{t.vehicle?.regNo}</div></div>
              <div><div className="k">Driver</div><div className="v" style={{ fontSize: 13 }}>{t.vehicle?.driverName}<br /><span className="muted small">{t.vehicle?.driverPhone}</span></div></div>
            </div>
          </div>

          {/* Step 1 — hatchery gate */}
          <div className={`stepcard ${t.packedAt ? 'done' : ''}`}>
            <h3>Step 1 · Packing at the hatchery {t.packedAt && '✓'}</h3>
            {t.packedAt
              ? (
                <div className="kv">
                  <div><div className="k">Bags loaded</div><div className="v">{t.bagsLoaded}</div></div>
                  <div><div className="k">Water</div><div className="v">{t.waterTempC ?? '—'}°C · {t.salinityPpt ?? '—'} ppt</div></div>
                  <div><div className="k">Logged</div><div className="v" style={{ fontSize: 13 }}>{fmtStamp(t.packedAt)}</div></div>
                </div>
                )
              : (
                <form onSubmit={submitPacking}>
                  <p className="muted small" style={{ marginTop: 0 }}>
                    Count the oxygenated bags as they go into the vehicle and check the water before the tanks are sealed.
                  </p>
                  <Field label="Bags loaded" required>
                    <input type="number" min="1" value={packing.bagsLoaded}
                      onChange={e => setPacking({ ...packing, bagsLoaded: e.target.value })} required />
                  </Field>
                  <Field label="Water temperature (°C)">
                    <input type="number" step="0.1" value={packing.waterTempC}
                      onChange={e => setPacking({ ...packing, waterTempC: e.target.value })} />
                  </Field>
                  <Field label="Salinity (ppt)">
                    <input type="number" step="0.5" value={packing.salinityPpt}
                      onChange={e => setPacking({ ...packing, salinityPpt: e.target.value })} />
                  </Field>
                  <Field label="Photo of the loaded vehicle">
                    <input type="file" accept="image/*" capture="environment" onChange={e => setPhoto(e.target.files?.[0] || null)} />
                  </Field>
                  <SubmitButton busy={busy} type="submit" className="btn primary block">Confirm packing & take gate pass</SubmitButton>
                </form>
                )}
            {t.packingPhotoPath && (
              <a href={t.packingPhotoPath} target="_blank" rel="noreferrer">
                <img src={t.packingPhotoPath} alt="Loading proof" style={{ width: '100%', borderRadius: 8, marginTop: 10, border: '1px solid var(--line)' }} />
              </a>
            )}
          </div>

          {/* Step 2 — on the road */}
          {t.packedAt && (
            <div className={`stepcard ${t.departedAt ? 'done' : ''}`}>
              <h3>Step 2 · On the road {t.departedAt && '✓'}</h3>
              {t.departedAt
                ? <div className="small">Departed {fmtStamp(t.departedAt)} · about {t.estHours} hours to the pond.</div>
                : (
                  <>
                    <p className="muted small" style={{ marginTop: 0 }}>Mark departure when the vehicle leaves the hatchery gate.</p>
                    <SubmitButton busy={busy} className="btn primary block" onClick={depart}>Vehicle has departed</SubmitButton>
                  </>
                  )}
            </div>
          )}

          {/* Step 3 — pond side */}
          {t.departedAt && (
            <div className={`stepcard ${t.deliveredAt ? 'done' : ''}`}>
              <h3>Step 3 · Handover at the pond {t.deliveredAt && '✓'}</h3>
              {t.deliveredAt
                ? (
                  <div className="kv">
                    <div><div className="k">Delivered</div><div className="v" style={{ fontSize: 13 }}>{fmtStamp(t.deliveredAt)}</div></div>
                    <div><div className="k">Damaged bags</div><div className="v">{t.damagedBags || 0}</div></div>
                    {t.conditionNotes && <div style={{ gridColumn: '1 / -1' }}><div className="k">Notes</div><div className="v" style={{ fontSize: 13 }}>{t.conditionNotes}</div></div>}
                  </div>
                  )
                : (
                  <form onSubmit={submitDelivery}>
                    <p className="muted small" style={{ marginTop: 0 }}>
                      Show the farmer the certificates below, let him count the bags, then enter the OTP sent to his phone.
                    </p>
                    <div className="btnrow" style={{ marginBottom: 12 }}>
                      {b?.pcr?.reportPath && <a className="btn sm" href={b.pcr.reportPath} target="_blank" rel="noreferrer">PCR certificate</a>}
                      <Badge tone="info">{b?.broodstockSource}</Badge>
                      <Badge tone="info">stress {b?.stressTestPct}%</Badge>
                    </div>
                    <Field label="Farmer's delivery OTP" required hint="Four digits, sent to the farmer's phone.">
                      <input className="otp-box" inputMode="numeric" maxLength={4} value={delivery.otp}
                        onChange={e => setDelivery({ ...delivery, otp: e.target.value.replace(/\D/g, '') })} required />
                    </Field>
                    <Field label="Damaged / burst bags">
                      <input type="number" min="0" max={t.bagsLoaded || undefined} value={delivery.damagedBags}
                        onChange={e => setDelivery({ ...delivery, damagedBags: e.target.value })} />
                    </Field>
                    <Field label="Condition notes">
                      <textarea value={delivery.conditionNotes}
                        onChange={e => setDelivery({ ...delivery, conditionNotes: e.target.value })}
                        placeholder="Anything the desk should know about the handover" />
                    </Field>
                    <SubmitButton busy={busy} type="submit" className="btn primary block">Complete the handover</SubmitButton>
                  </form>
                  )}
            </div>
          )}

          <div className="stepcard">
            <h3>Order</h3>
            <div className="kv">
              <div><div className="k">Order</div><div className="v mono" style={{ fontSize: 13 }}>{o?.orderCode}</div></div>
              <div><div className="k">Value</div><div className="v">{inr(o?.totalAmount)}</div></div>
              <div><div className="k">Paid</div><div className="v">{o?.balancePaidAt ? 'In full' : 'Advance only'}</div></div>
            </div>
          </div>
        </div>
      </div>
    </Page>
  )
}
